import { useCallback, useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import {
  Label,
  SnackbarApiErrorMessage,
  SubSection,
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import {
  ButtonContainer,
  IconLibrary,
  PermissionButton,
} from '@product-framework/index';
import { FormattedMessage } from 'react-intl';
import Form from '@product-framework/components/AdvancedForms/Form';
import editSchema from './edit.json';
import locationEditSchema from './location_edit.json';
import validationSchema from './validations';

const FORM_ID = 'ServiceProviderDispatchNotificationsForm';
const PERMISSION_SP = 'SERVICEPROVIDERNOTIFICATION';

function DispatchNotifications({
  onChange = () => undefined,
  config,
  isLocationScope,
}) {
  const { formatMessage } = useLocalization();
  const formRef = useRef();
  const snackbar = useSnackbar();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get, post } = useDataProvider(provider);

  const emailUrl = config?.api?.setup?.emailUrl;
  const emailSaveSuccess = config?.ui?.messages?.setup?.emailSaveSuccess;

  const schema = useMemo(
    () => (isLocationScope ? locationEditSchema : editSchema),
    [isLocationScope],
  );

  function preProcessResponseData(responseData) {
    const list = responseData?.data;
    if (Array.isArray(list) && list.length > 0) {
      return { email: list[0]?.email || '' };
    }
    return { email: '' };
  }

  const fetchDefaultValues = useCallback(async () => {
    try {
      const response = await get(emailUrl);
      if (response?.ok) {
        return {
          data: preProcessResponseData(response?.data),
          errors: null,
        };
      }
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={response?.errors} />,
      });
      return {
        data: { email: '' },
        errors: response?.errors || null,
      };
    } catch (err) {
      return {
        data: null,
        errors: [{ message: err.message }],
      };
    }
  }, [emailUrl]);

  const onSubmit = async data => {
    try {
      const response = await post(emailUrl, { email: data.email });
      if (response?.ok) {
        snackbar.showMessage(formatMessage(emailSaveSuccess));
        return {
          nextValues: preProcessResponseData(response?.data),
          errors: null,
          callback: () => onChange(),
        };
      }
      return {
        nextValues: data,
        errors: response?.errors,
      };
    } catch (err) {
      return {
        nextValues: data,
        errors: [{ message: err.message }],
      };
    }
  };

  return (
    <Box>
      <Label
        htmlFor="dispatchNotifications"
        style={{ display: 'block', marginBottom: '5px' }}>
        {formatMessage({
          id: 'DISPATCH_NOTIFICATION_EMAIL',
          defaultMessage: 'Dispatch Notification Email',
        })}
      </Label>
      <SubSection>
        <Form
          formId={FORM_ID}
          ref={formRef}
          permissionEntities={[PERMISSION_SP]}
          onSubmit={onSubmit}
          schema={schema}
          fetchDefaultValues={fetchDefaultValues}
          validationSchema={validationSchema(formatMessage, isLocationScope)}
        />
        <ButtonContainer>
          <PermissionButton
            permissionName="CAN_UPDATE_SP_DISPATCH_NOTIFICATIONS"
            variant="contained"
            startIcon={<IconLibrary.Save />}
            color="primary"
            onClick={() => formRef.current?.submit({ formId: FORM_ID })}>
            <FormattedMessage id="SAVE" defaultMessage="Save" />
          </PermissionButton>
          <PermissionButton
            permissionName="CAN_UPDATE_SP_DISPATCH_NOTIFICATIONS"
            variant="contained"
            color="primary"
            startIcon={<IconLibrary.Reset />}
            onClick={() => formRef.current?.reset()}>
            <FormattedMessage id="RESET" defaultMessage="Reset" />
          </PermissionButton>
        </ButtonContainer>
      </SubSection>
    </Box>
  );
}

export default DispatchNotifications;
